import type { definitions, paths } from '@luxuryescapes/contract-svc-lux-loyalty';

// Contract-based types
export type CustomRule = definitions['presentedCustomRule'];
export type CustomRuleInput = definitions['customRule'];
export type CustomRuleType = definitions['customRule']['type'];
export type CustomRulePurpose = definitions['customRule']['purpose'];
export type PointsType = definitions['customRule']['pointsType'];
export type ProductType = definitions['customRule']['productType'];

// include status filtering
export type CustomRuleSearchParams =
  paths['/api/lux-loyalty/custom-rules/search']['post']['parameters']['body']['payload'] & {
    status?: 'all' | 'active' | 'scheduled' | 'expired';
  };

// include counts in the search response
export type CustomRuleSearchResponse =
  paths['/api/lux-loyalty/custom-rules/search']['post']['responses']['200']['content']['application/json'] & {
    result: {
      customRules: Array<definitions['presentedCustomRule']>;
      pageInfo: PageInfo;
      counts: CustomRuleStatusCounts;
    };
  };

export interface CustomRuleStatusCounts {
  total: number;
  active: number;
  scheduled: number;
  expired: number;
}

export type CreateCustomRuleRequestBody =
  paths['/api/lux-loyalty/custom-rules']['post']['parameters']['body']['payload'];
export type CreateCustomRuleResponse =
  paths['/api/lux-loyalty/custom-rules']['post']['responses']['201']['content']['application/json'];

export type UpdateCustomRuleRequestBody =
  paths['/api/lux-loyalty/custom-rules/{customRuleId}']['patch']['parameters']['body']['updateCustomRulesBody'];
export type UpdateCustomRuleResponse =
  paths['/api/lux-loyalty/custom-rules/{customRuleId}']['patch']['responses']['200']['content']['application/json'];

export type PageInfo = {
  pageNumber: number;
  pageSize: number;
  pageCount: number;
  totalRecords: number;
};

export const PRODUCT_TYPE_OPTIONS: Array<ProductType> = [
  'hotel_flash',
  'hotel_lpc',
  'hotel_lpp_eps',
  'le_tour',
  'tour_bank',
  'trusted_partner_tour',
  'tour_optional_extra',
  'experience',
  'cruise_group_1',
  'cruise_group_2',
  'cruise_group_3',
  'dom_economy',
  'dom_business',
  'int_economy',
  'int_premium_economy',
  'int_business',
  'int_first',
  'lux_plus',
  'gift_card',
  'car_hire',
  'rental',
  'cfmr',
  'insurance',
  'shop_tier_1',
  'shop_tier_2',
  'shop_tier_3',
  'shop_tier_4',
  'shop_tier_5',
  'shop_shipping',
];

export const CUSTOM_RULE_TYPE_OPTIONS: Array<CustomRuleType> = [
  'BONUS_POINTS',
  'BONUS_POINTS_ONLY',
  'MULTIPLE_ON_BASE_POINTS',
];

export const CUSTOM_RULE_PURPOSE_OPTIONS: Array<CustomRulePurpose> = [
  'ESTIMATION_ONLY',
  'ESTIMATION_AND_EARN',
  'ACCRUAL_OVERRIDE',
];

export const POINTS_TYPE_OPTIONS: Array<PointsType> = [
  'BASE_POINTS',
  'BONUS_POINTS',
  'STATUS_CREDITS',
  'BONUS_STATUS_CREDITS',
];

// Manual Accruals types
export type ManualAccrualStatus = 'PENDING' | 'PROCESSED' | 'FAILED';

export type PendingPointsStatus = 'AWAITING_PAYMENT' | 'COMPLETED_AWAITING_TRAVEL' | 'CANCELLED' | 'MARK_AS_FRAUD';

export interface CustomRuleApplied {
  id: string;
  name: string;
}

export interface PendingPoints {
  id: string;
  status: PendingPointsStatus;
  points: number;
  bonusPoints: number;
  statusCredits: number;
  customRulesApplied: Array<CustomRuleApplied>;
}

export interface ManualAccrual {
  id: string;
  customerId: string;
  orderId: string;
  itemId: string;
  productType: string;
  bonusMechanic: number;
  contractPrice: number;
  marginPackage?: number;
  packageId?: string;
  offerId?: string;
  numberOfNights?: number;
  payload: Record<string, unknown>;
  manualAccrualStatus: ManualAccrualStatus;
  createdAt: string;
  updatedAt: string;
  processedAt?: string;
  errorMessage?: string;
  pendingPoints?: PendingPoints;
  bookingDate: string;
  pointsEarnDate: string;
  pointsFromEstimate: number;
  bonusPointsFromEstimate: number;
  statusCreditsFromEstimate: number;
}

export interface ManualAccrualsResponse {
  status: 200;
  message: string;
  pageInfo: PageInfo;
  result: Array<ManualAccrual>;
}

export interface ManualAccrualFilterParams {
  page?: number;
  pageSize?: number;
  status?: ManualAccrualStatus;
  customerId?: string;
  orderId?: string;
  itemId?: string;
  productType?: ProductType;
}

export interface ManualAccrualsRetryRequestBody {
  itemIds?: Array<string>;
  all?: boolean;
  productType?: ProductType;
  estimateOnly?: boolean;
  force?: boolean;
  includeFailed?: boolean;
  reestimatePendingPoints?: boolean;
  reimportBigQueryData?: boolean;
  skipEstimateBeforeAccrual?: boolean;
  skipAccrual?: boolean;
  useRevisedValidation?: boolean;
}

export interface ManualAccrualsRetryResponse {
  status: 200;
  message: string;
  result: {
    totalItemsCount: number;
    failedItemsCount: number;
    successItems: Array<string>;
    failedItems: Array<string>;
  };
}

// Loyalty Account types
export type LoyaltyTier = 'bronze' | 'silver' | 'gold' | 'platinum';

export interface LoyaltyAccountSummary {
  id: string;
  customerId: string;
  ibsMemberId: string;
  accountNumber: number;
  tier: LoyaltyTier;
  points: number;
  statusCredits: number;
  benefits: Array<any>;
  totalCreditsForTierUpgrade?: number | null;
  totalCreditsForTierRetention?: number | null;
  statusReviewDate: string;
  numberOfNights: number;
  numberOfNightsForTierUpgrade?: number | null;
  numberOfNightsForTierRetention?: number | null;
  region: string;
}

export interface GetLoyaltyAccountResponse {
  status: 200;
  message: string;
  result: {
    accountSummary: LoyaltyAccountSummary | null;
  };
}

export interface CreateLoyaltyAccountRequest {
  customerId: string;
  givenName?: string;
  familyName?: string;
  region?: string;
}

export interface CreateLoyaltyAccountResponse {
  status: 201;
  message: string;
  result: {
    accountSummary: LoyaltyAccountSummary;
  };
}

export interface CalculateEarnRequest {
  customerId?: string;
  publicPrice: number;
  luxPlusPrice?: number;
  luxPlusMember: boolean;
  currency: string;
  mx?: string;
  region?: string;
  productDetails: {
    loyaltyProductType: ProductType;
    offerId?: string;
    packageId?: string;
    promoKey?: string;
    earnKey?: string;
  };
}

export interface CustomRuleBreakdown {
  id: string;
  name: string;
  type: 'BONUS_POINTS_ONLY' | 'MULTIPLE_ON_BASE_POINTS' | 'BONUS_POINTS';
  pointsType: 'Base Points' | 'Status Credits' | 'Bonus Points' | 'Bonus Status Credits';
  points: number;
}

export interface PointDetail {
  pointType: 'Base Points' | 'Status Credits' | 'Bonus Points' | 'Bonus Status Credits';
  points: number;
}

export interface CalculateEarnResponse {
  status: 200;
  message: string;
  result: {
    totalBasePoints: number;
    totalStatusCredits: number;
    totalBonusPoints: number;
    totalBonusStatusCredits: number;
    totalPointsEarnRatio: number;
    roundingEnabled: boolean;
    pointDetails: Array<PointDetail>;
    appliedCustomRuleIds: Array<string>;
    customRulesBreakdown: Array<CustomRuleBreakdown>;
  };
}

// Constants
export const MIN_START_DATE = '2025-04-01';
export const MAX_END_DATE = '2099-12-31';
export const DEFAULT_PAGE_SIZE = 10;

export const MANUAL_ACCRUAL_STATUS_OPTIONS: Array<ManualAccrualStatus> = ['PENDING', 'PROCESSED', 'FAILED'];
